import { createFileRoute, Link } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { Suspense, useMemo, useState } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Layout } from "@/components/Layout";
import { SEOHead } from "@/components/SEOHead";
import { NumberRow } from "@/components/NumberPill";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StateLogo } from "@/components/StateLogo";
import { JackpotCard } from "@/components/JackpotCard";
import { JackpotFilters } from "@/components/JackpotFilters";
import { WinnerMap } from "@/components/WinnerMap";
import { useLatestResults, useAllStates } from "@/hooks/use-query-hooks";
import { latestResultsQueryOptions, type LatestResultDTO } from "@/lib/latest-results-ssr";
import { jackpotsQueryOptions } from "@/lib/jackpots-ssr";
import { formatDate } from "@/lib/format";
import { MapPin, ArrowRight, Sparkles, Calculator, BarChart3, CheckCircle, Coins } from "lucide-react";
import { isToday, isThisWeek, parseISO } from "date-fns";
import ogDefault from "@/assets/og-default.jpg";
import { HOME_FAQS } from "@/data/site-faqs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export const Route = createFileRoute("/")({
  loader: async ({ context }) => {
    // Both lists are rendered above the fold — resolving them during SSR keeps
    // the first paint identical to the hydrated view (no skeleton flash / CLS).
    await Promise.all([
      context.queryClient.ensureQueryData(latestResultsQueryOptions(15)),
      context.queryClient.ensureQueryData(jackpotsQueryOptions()),
    ]);
  },
  head: () => buildSeo({
    title: "USA Lottery Club — Live Jackpots, Results & Tools",
    description:
      "Check the latest US lottery jackpots, winning numbers, and results. Free tools for number generation, results checking, and tax estimation.",
    path: "/",
    image: ogDefault,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: HOME_FAQS.map((f) => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: { "@type": "Answer", text: f.answer },
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "USA Lottery Club",
        url: "/",
        description:
          "Live US lottery jackpots, winning numbers, and free tools for players in all 50 states.",
      },
    ],
  }),
  component: IndexPage,
});

function IndexPage() {
  const { data: jackpots } = useSuspenseQuery(jackpotsQueryOptions());
  const { data: latestResults } = useLatestResults(1000);
  const { data: allStates } = useAllStates();

  const latestByGame = useMemo(() => {
    const map = new Map<string, any>();
    for (const r of (latestResults || []) as any[]) {
      const gid = r.game_id ?? r.games?.id;
      if (!gid) continue;
      const existing = map.get(gid);
      if (!existing || new Date(r.draw_date) > new Date(existing.draw_date)) {
        map.set(gid, r);
      }
    }
    return map;
  }, [latestResults]);

  const statesWithLottery = allStates?.filter((s) => s.has_lottery) || [];

  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const [gameFilter, setGameFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");

  const hasActiveFilters =
    search !== "" || stateFilter !== "all" || gameFilter !== "all" || dateFilter !== "all";
  const clearFilters = () => {
    setSearch("");
    setStateFilter("all");
    setGameFilter("all");
    setDateFilter("all");
  };

  const deduplicatedJackpots = useMemo(() => {
    if (!jackpots) return [];
    const filtered = jackpots.filter((j) => j.jackpot_amount > 0 || j.cash_value);
    return filtered
      .map((j) => {
        const game = j.games as any;
        const stateInfo = game?.states;
        return { best: j, allStates: stateInfo ? [stateInfo] : [] };
      })
      .sort((a, b) => b.best.jackpot_amount - a.best.jackpot_amount);
  }, [jackpots]);

  const gameNames = useMemo(
    () => deduplicatedJackpots.map((d) => ({ name: (d.best.games as any)?.name || "" })),
    [deduplicatedJackpots],
  );

  const filteredJackpots = useMemo(() => {
    return deduplicatedJackpots.filter(({ best: j, allStates: states }) => {
      const game = j.games as any;
      const name: string = game?.name || "";
      if (search && !name.toLowerCase().includes(search.toLowerCase())) return false;
      if (stateFilter !== "all") {
        const matchesState = states.some((s: any) => s.abbreviation === stateFilter);
        if (!matchesState && !game?.is_multistate) return false;
      }
      if (gameFilter !== "all" && name !== gameFilter) return false;
      if (dateFilter !== "all" && j.next_draw_datetime) {
        const drawDate = parseISO(j.next_draw_datetime);
        if (dateFilter === "today" && !isToday(drawDate)) return false;
        if (dateFilter === "week" && !isThisWeek(drawDate)) return false;
      }
      return true;
    });
  }, [deduplicatedJackpots, search, stateFilter, gameFilter, dateFilter]);

  return (
    <Layout>
      <SEOHead
        title="USA Lottery Club — Live Jackpots, Results & Tools"
        description="Check the latest US lottery jackpots, winning numbers, and results."
      />

      <section className="container py-6 md:py-10">
        <div className="flex items-start gap-3 mb-4">
          <span
            className="material-symbols-outlined text-accent shrink-0 !text-[72px] leading-none md:!text-[40px]"
            aria-hidden="true"
          >
            money_bag
          </span>


          <h1 className="font-sans text-2xl md:text-3xl font-bold">
            US Lottery Jackpots, Results &amp; Tools
          </h1>
        </div>
        <p className="text-muted-foreground mb-4 max-w-3xl">
          Track live lottery jackpots, winning numbers, and draw results for Powerball, Mega Millions,
          and every US state lottery. Use our free tools to generate numbers, check results, analyze
          frequency trends, and estimate lottery taxes.
        </p>
        <h2 className="font-sans text-lg md:text-xl font-bold mb-3">Current Jackpots</h2>

        <JackpotFilters
          search={search}
          onSearchChange={setSearch}
          stateFilter={stateFilter}
          onStateFilterChange={setStateFilter}
          gameFilter={gameFilter}
          onGameFilterChange={setGameFilter}
          dateFilter={dateFilter}
          onDateFilterChange={setDateFilter}
          states={statesWithLottery as any}
          games={gameNames}
          hasActiveFilters={hasActiveFilters}
          onClear={clearFilters}
        />

        {filteredJackpots.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">No jackpots match your filters.</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredJackpots.map(({ best: j, allStates: states }, idx) => {
              const game = j.games as any;
              const gameLink = game?.is_multistate
                ? `/games/${game.slug}`
                : game?.states
                  ? `/${game.states.slug}/${game.slug}`
                  : `/games/${game.slug}`;
              return (
                <JackpotCard
                  key={j.id}
                  game={game}
                  jackpotAmount={j.jackpot_amount}
                  cashValue={j.cash_value}
                  nextDrawDatetime={j.next_draw_datetime}
                  allStates={states}
                  isMultistate={game?.is_multistate || states.length > 1}
                  gameLink={gameLink}
                  latestResult={game?.id ? latestByGame.get(game.id) : undefined}
                  priority={idx === 0}
                />
              );
            })}
          </div>
        )}
      </section>

      <section className="container py-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-sans text-xl md:text-2xl font-bold">Latest Results</h2>
        </div>
        <Suspense fallback={<LatestResultsSkeleton />}>
          <LatestResultsList />
        </Suspense>
      </section>

      <WinnerMap />

      <section className="container py-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-sans text-xl md:text-2xl font-bold">
            <MapPin className="inline h-5 w-5 mr-2 text-primary" />
            Browse by State
          </h2>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/states">
              View All <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
          {statesWithLottery.map((s) => (
            <Link key={s.id} to="/states/$stateSlug" params={{ stateSlug: s.slug }}>
              <div className="rounded-lg border bg-card p-2 text-center hover:bg-secondary transition-colors flex flex-col items-center gap-1">
                <StateLogo stateSlug={s.slug} abbreviation={s.abbreviation} size="sm" />
                <span className="font-sans font-bold text-xs">{s.abbreviation}</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="container py-6">
        <h2 className="font-sans text-xl md:text-2xl font-bold mb-6">Lottery Tools</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { to: "/tools/number-generator", icon: Sparkles, title: "Number Generator", desc: "Generate random or strategy-based lottery numbers" },
            { to: "/tools/results-checker", icon: CheckCircle, title: "Results Checker", desc: "Check your numbers against winning draws" },
            { to: "/tools/tax-calculator", icon: Calculator, title: "Tax Calculator", desc: "Estimate take-home after federal & state taxes" },
            { to: "/tools/frequency-analyzer", icon: BarChart3, title: "Frequency Analyzer", desc: "See hot, cold, and overdue numbers" },
            { to: "/tools/payout-calculator", icon: Coins, title: "Payout Calculator", desc: "Lump-sum vs annuity by state & filing status" },
          ].map(({ to, icon: Icon, title, desc }) => (
            <Link key={to} to={to}>
              <Card className="hover:shadow-md transition-shadow h-full">
                <CardContent className="pt-6">
                  <Icon className="h-8 w-8 text-primary mb-3" />
                  <h3 className="font-sans font-bold mb-1">{title}</h3>
                  <p className="text-sm text-muted-foreground">{desc}</p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </section>

      <section className="container py-8 md:py-12">
        <h2 className="font-sans text-xl md:text-2xl font-bold mb-2">
          Frequently Asked Questions
        </h2>
        <p className="text-sm text-muted-foreground mb-6 max-w-3xl">
          Answers to common questions about US lottery jackpots, draws, odds, taxes, and how to play.
        </p>
        <Accordion type="single" collapsible className="max-w-3xl">
          {HOME_FAQS.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`}>
              <AccordionTrigger className="text-left font-semibold">{f.question}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{f.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </Layout>
  );
}

function LatestResultsList() {
  const { data } = useSuspenseQuery(latestResultsQueryOptions(15));
  return (
    <div className="space-y-3">
      {data.map((r: LatestResultDTO) => (
        <Card key={r.id}>
          <CardContent className="py-3 px-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-sans font-semibold text-sm">{r.games?.name}</span>
                <span className="text-xs text-muted-foreground">{formatDate(r.draw_date)}</span>
              </div>
              <NumberRow
                numbers={r.winning_numbers || []}
                bonusNumbers={r.bonus_numbers || []}
                multiplier={r.multiplier}
                size="sm"
                align="left"
              />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function LatestResultsSkeleton() {
  return (
    <div className="space-y-3" aria-busy="true" aria-live="polite">
      {Array.from({ length: 15 }).map((_, i) => (
        <Card key={i}>
          <CardContent className="py-3 px-4">
            <div className="h-4 w-40 rounded bg-muted animate-pulse mb-2" />
            <div className="flex gap-2">
              {Array.from({ length: 6 }).map((_, j) => (
                <div key={j} className="h-7 w-7 rounded-full bg-muted animate-pulse" />
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}